/**
 * 20260903 - TEXT-FIELD AUTO-FOCUS POLICY.
 *
 * Auto-focusing a text field is a courtesy with a hardware keyboard and a hazard without one: on an
 * iPhone, or an iPad with no keyboard attached, the software keyboard rises over the dialog the user
 * has not read yet. So the owner's rule is "auto-focus only on macOS, and on iPadOS with a keyboard".
 *
 * 20260923 - AND THE BOARD'S TILE ACTION BAR, which asks a second question of the same host: is there
 * a POINTER (`hwptr`)? See boardHoverTakeoverApplies below. Two policies, one fragment, because they
 * are two readings of the same fact — what is plugged into this device right now.
 *
 * WHAT THE HOST TELLS US, and how it gets here. Platform and form factor are FIXED for the life of a
 * WebView session, so they ride the URL for the same reason the theme and the build info do
 * (config/appInfo.ts, theme/macTitlebarInset.ts): readable before first paint, no ordering hazard
 * against a push that lands after the first render. `hwkbd` and `hwptr` seed from the URL too, but
 * they are the values that can CHANGE mid-session (a Magic Keyboard — or its trackpad — is attached
 * or detached), so Unity also pushes `setInputCapabilities` — bridge/features/settings.ts — on every
 * change, into the leaf store that useShouldAutoFocus() and useBoardHoverTakeover() read
 * (inputCapabilitiesStore.ts). Each push carries only the key that changed, and the store merges.
 *
 * EVERY field is optional. Standalone, online review and the `?page=debug` harness carry no params at
 * all, and the bare `?embedded=1` Timeline route carries no platform either — so the no-platform
 * branch has to answer for a real browser, not just for a missing host.
 *
 * Unity writers: TimelineWebViewPanel.GetDeviceInputQueryFragment(), appended by
 * GetEmbeddedQueryFragment, ScreenplayManagerPopupPanel's four URL builders,
 * PurchasingWebViewPopupPanel.GetPageQueryFragment (the ?page=projects host) and
 * AvatarCustomizerWebViewPopupPanel (the Character Creator).
 *
 * 20260923 - A FIFTH PARAM, `osMajor` (OS_MAJOR_URL_PARAM below): the iOS major version, appended by
 * the same fragment ONLY when native knows it — a real iOS device. It is FIXED for the session like
 * platform and form factor, so it rides the URL and is never pushed. Its one reader today is
 * ipadLegacyMultitaskingApplies, published before first render by platform/hostOsFlags.ts.
 */

export type HostPlatform = 'ios' | 'macos';

/** 20260923 - the query param carrying the iOS major version. ONE spelling for both ends of the seam:
 *  nativeHostOsUrlContract.test.ts reads TimelineWebViewPanel.GetDeviceInputQueryFragment() and
 *  demands `"&" + this + "="` of it, so a rename on either side alone fails that suite. */
export const OS_MAJOR_URL_PARAM = 'osMajor';
/** desktop = macOS/Windows/Editor, tablet = iPad-class, phone = everything else on iOS. */
export type HostFormFactor = 'desktop' | 'tablet' | 'phone';

export interface HostInput {
  platform?: HostPlatform;
  formFactor?: HostFormFactor;
  hardwareKeyboard?: boolean;
  /** 20260923 - is a trackpad or mouse attached? The SECOND hot-pluggable fact on this fragment
   *  (`hwptr`), pushed on change exactly like `hwkbd`. iPadOS reports a mouse and a Magic Keyboard
   *  trackpad alike as a GCMouse, so this is one flag for both. Absent means an older Unity binary
   *  that never sent it — see boardHoverTakeoverApplies for what absence has to mean. */
  pointerDevice?: boolean;
  /** 20260923 - the iOS MAJOR version (18 for 18.7). iOS device only: absent on macOS, Windows, the
   *  Editor, standalone/online review, and on every older Unity binary that never sent it. A
   *  positive integer or absent — never 0. FIXED for the session, so never pushed
   *  (setInputCapabilities carries no such key). */
  osMajor?: number;
}

/** Pure parse, exported for tests and for anything that already holds a query string.
 *  An absent OR unrecognised value collapses to `undefined` — one "unknown" for callers, so a
 *  future Unity value we do not know yet degrades to the no-platform branch instead of a wrong
 *  answer. */
export function readHostInput(search: string): HostInput {
  const params = new URLSearchParams(search);

  const platform = params.get('platform');
  const formFactor = params.get('formFactor');
  const hwkbd = params.get('hwkbd');
  const hwptr = params.get('hwptr');
  const osMajor = params.get(OS_MAJOR_URL_PARAM);

  const out: HostInput = {};
  if (platform === 'ios' || platform === 'macos') out.platform = platform;
  if (formFactor === 'desktop' || formFactor === 'tablet' || formFactor === 'phone') out.formFactor = formFactor;
  if (hwkbd === '1' || hwkbd === '0') out.hardwareKeyboard = hwkbd === '1';
  if (hwptr === '1' || hwptr === '0') out.pointerDevice = hwptr === '1';
  // Digits only, then > 0: "18.7", "-1", "abc" and "" never reach Number(), and "0" (native's own
  // "unknown") collapses to absent with them — one "unknown" for callers, as above.
  if (osMajor !== null && /^\d+$/.test(osMajor)) {
    const major = Number(osMajor);
    if (major > 0) out.osMajor = major;
  }
  return out;
}

/** Touch-only: a coarse pointer that cannot hover. The guard is not defensive dressing — jsdom has
 *  no matchMedia in several suites, and a throw here would take out whatever dialog asked. */
function isTouchOnlyPointer(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  try {
    return window.matchMedia('(pointer: coarse) and (hover: none)').matches;
  } catch {
    return false;
  }
}

/**
 * The policy itself.
 *
 * macOS: always — there is a keyboard by definition.
 * iOS: iPad-class ONLY, and only while a hardware keyboard is attached. An iPhone never qualifies,
 *      keyboard or not: the owner named three dialogs, all of which are cramped against a raised
 *      software keyboard on a phone.
 * No platform param (standalone, online review, `?page=debug`): a real browser. Focus unless the
 *      pointer says this is a touch-only device, which is the same judgement made from the outside.
 */
export function shouldAutoFocusTextField(input: HostInput): boolean {
  if (input.platform === 'macos') return true;
  if (input.platform === 'ios') return input.formFactor === 'tablet' && input.hardwareKeyboard === true;
  return isTouchOnlyPointer() === false;
}

/**
 * 20260923 - THE BOARD'S TILE ACTION BAR: may the selected tile's 3D/Enhance bar WAIT for a hover?
 *
 * The owner: "now that we've added back in showing the 3D/Sparkle buttons on a selected shot on
 * mouse hover, let's also have those buttons show on iPadOS when a shot is selected but no
 * trackpad/mouse is attached. So for iPadOS, if a trackpad/mouse is connected we only show on mouse
 * hover... when one isn't connected we show it when a shot is selected (like in compact mode)".
 *
 * TRUE means the takeover applies: with the shot actions panel open the selected tile's bar comes
 * down, and `data-mouse-hover` is what brings it back (SceneBoardExpanded.module.css). FALSE means
 * the tile keeps `.selected .actions` — the compact layout's behaviour — because there is no pointer
 * on this device to hover WITH, and a bar that only a hover can reveal is a bar with no way in.
 *
 * `live` is the pushed value and OUTRANKS the URL seed: a trackpad attached or detached with the
 * board open arrives as setInputCapabilities, and the seed is only the state at page load.
 *
 * macOS: always — there is a pointer by definition.
 * iPad: the pointer flag decides, and UNDEFINED means TRUE. Absence here is an old Unity binary that
 *      sends no `hwptr`, never "no pointer": answering false there would take the hover reveal away
 *      from a trackpad iPad, which is the behaviour that shipped. Absence must cost nothing.
 * iPhone: true, and INERT — compact layouts have no shot actions panel, so `panelOwnsThisShot` is
 *      never true there and nothing reads this. Stated rather than left to fall through so the phone
 *      row is an answer and not an accident.
 * No platform (standalone, online review, `?page=debug`): a real browser, judged from the outside
 *      exactly as the auto-focus policy judges it — a touch-only pointer cannot hover.
 */
export function boardHoverTakeoverApplies(input: HostInput, live: { pointerDevice?: boolean } = {}): boolean {
  if (input.platform === 'macos') return true;
  if (input.platform === 'ios') {
    if (input.formFactor !== 'tablet') return true;
    return (live.pointerDevice ?? input.pointerDevice) ?? true;
  }
  return isTouchOnlyPointer() === false;
}

/**
 * 20260923 - THE iPadOS 15-18 MULTITASKING PILL: does the board's top bar need a top inset for it?
 *
 * Owner device report (an iPad on iPadOS 18): the system draws its top-centre "…" multitasking
 * control over every multitasking-capable app window — full screen, Split View, Slide Over, Stage
 * Manager alike. The player hides the status bar, so env(safe-area-inset-top) is 0 on an un-notched
 * iPad and the pill lands squarely on the "Boards" tab. The pill arrived in iPadOS 15 and is GONE in
 * iPadOS 26 (Windowed Apps replaced it with the leading traffic-light cluster; 26.1 brought Slide
 * Over back through the green window button, with no pill). So: iPad AND osMajor < 26.
 *
 * TRUE means platform/hostOsFlags.ts sets `data-ipad-legacy-multitasking` on <html>, which adds the
 * top inset and switches OFF the 72px iPadOS-26 Home clearance (SceneBoardPage.module.css).
 *
 * ABSENT osMajor is FALSE, and that is the status quo, not a guess: an older Unity binary, the
 * Editor, macOS — every host that never sent the param — keeps exactly the bar it had yesterday.
 * Guessing "legacy" on silence would move the bar on every iPadOS 26 device running an old build.
 *
 * NO LOWER BOUND at 15: the app's floor is iOS 17.0, so every iPad that can run this is ≥ 17 and
 * already has the pill. `< 26` alone is exact.
 *
 * `tablet` is the EXISTING formFactor param (Unity's iPad-class answer, 20260903), not a new idiom
 * param: it is already the one iPad signal on this fragment, and a second spelling of the same
 * fact would be a second thing to keep in sync across the seam.
 */
export function ipadLegacyMultitaskingApplies(input: HostInput): boolean {
  return input.platform === 'ios'
    && input.formFactor === 'tablet'
    && typeof input.osMajor === 'number'
    && input.osMajor < 26;
}

// Parsed once per distinct query string, exactly as getAppInfo() does: the embedded URL never
// changes mid-session, so in the app this parses once — but keying on the search string keeps the
// accessor honest for anything that navigates, and testable without module-registry surgery.
let cached: { search: string; input: HostInput } | null = null;

export function getHostInput(): HostInput {
  const search = typeof window === 'undefined' ? '' : window.location.search;
  if (cached !== null && cached.search === search) return cached.input;
  cached = { search, input: readHostInput(search) };
  return cached.input;
}
